import React, { useCallback, useEffect, useRef, useState } from "react";

const parsePanelNumber = (value, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d+(\.\d+)?px$/.test(trimmed)) return Number.parseFloat(trimmed);
    if (/^\d+(\.\d+)?$/.test(trimmed)) return Number.parseFloat(trimmed);
  }
  return fallback;
};

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

const LiveFloatingPanel = ({
  isOpen,
  title,
  onClose,
  children,
  defaultPosition,
  defaultSize,
  minWidth = 320,
  minHeight = 260,
  zIndex = 2200,
  className = "",
  disableDragResize = false,
  closeDurationMs = 220,
  onLayoutChange = null,
}) => {
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const layoutFrameRef = useRef(null);
  const resizeNotifyTimerRef = useRef(null);
  const dragSessionRef = useRef(null);
  const resizeSessionRef = useRef(null);
  const liveLayoutRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [panelLayout, setPanelLayout] = useState(null);

  useEffect(() => {
    if (document.getElementById("live-floating-panel-ui-styles")) return;
    const style = document.createElement("style");
    style.id = "live-floating-panel-ui-styles";
    style.innerHTML = `
      .live-floating-panel.ui-resizable {
        overflow: hidden;
      }
      .live-floating-panel .ui-resizable-handle {
        position: absolute;
        z-index: 6;
      }
      .live-floating-panel-resize-handle {
        position: absolute;
        z-index: 6;
      }
      .live-floating-panel-resize-handle--n,
      .live-floating-panel-resize-handle--s {
        left: 14px;
        right: 14px;
        height: 10px;
      }
      .live-floating-panel-resize-handle--n {
        top: 0;
        cursor: n-resize;
      }
      .live-floating-panel-resize-handle--s {
        bottom: 0;
        cursor: s-resize;
      }
      .live-floating-panel-resize-handle--e,
      .live-floating-panel-resize-handle--w {
        top: 14px;
        bottom: 14px;
        width: 10px;
      }
      .live-floating-panel-resize-handle--e {
        right: 0;
        cursor: e-resize;
      }
      .live-floating-panel-resize-handle--w {
        left: 0;
        cursor: w-resize;
      }
      .live-floating-panel-resize-handle--se,
      .live-floating-panel-resize-handle--sw,
      .live-floating-panel-resize-handle--ne,
      .live-floating-panel-resize-handle--nw {
        width: 16px;
        height: 16px;
      }
      .live-floating-panel-resize-handle--se {
        right: 2px;
        bottom: 2px;
        cursor: se-resize;
      }
      .live-floating-panel-resize-handle--sw {
        left: 2px;
        bottom: 2px;
        cursor: sw-resize;
      }
      .live-floating-panel-resize-handle--ne {
        right: 2px;
        top: 2px;
        cursor: ne-resize;
      }
      .live-floating-panel-resize-handle--nw {
        left: 2px;
        top: 2px;
        cursor: nw-resize;
      }
      .live-floating-panel-resize-handle--se::before {
        content: "";
        position: absolute;
        right: 2px;
        bottom: 2px;
        width: 10px;
        height: 10px;
        border-right: 2px solid rgba(255,255,255,0.9);
        border-bottom: 2px solid rgba(255,255,255,0.9);
        opacity: 0.9;
      }
      .live-floating-panel .ui-resizable-n,
      .live-floating-panel .ui-resizable-s {
        left: 14px;
        right: 14px;
        height: 10px;
      }
      .live-floating-panel .ui-resizable-n {
        top: 0;
        cursor: n-resize;
      }
      .live-floating-panel .ui-resizable-s {
        bottom: 0;
        cursor: s-resize;
      }
      .live-floating-panel .ui-resizable-e,
      .live-floating-panel .ui-resizable-w {
        top: 14px;
        bottom: 14px;
        width: 10px;
      }
      .live-floating-panel .ui-resizable-e {
        right: 0;
        cursor: e-resize;
      }
      .live-floating-panel .ui-resizable-w {
        left: 0;
        cursor: w-resize;
      }
      .live-floating-panel .ui-resizable-se,
      .live-floating-panel .ui-resizable-sw,
      .live-floating-panel .ui-resizable-ne,
      .live-floating-panel .ui-resizable-nw {
        width: 16px;
        height: 16px;
      }
      .live-floating-panel .ui-resizable-se {
        right: 2px;
        bottom: 2px;
        cursor: se-resize;
      }
      .live-floating-panel .ui-resizable-sw {
        left: 2px;
        bottom: 2px;
        cursor: sw-resize;
      }
      .live-floating-panel .ui-resizable-ne {
        right: 2px;
        top: 2px;
        cursor: ne-resize;
      }
      .live-floating-panel .ui-resizable-nw {
        left: 2px;
        top: 2px;
        cursor: nw-resize;
      }
      .live-floating-panel .ui-resizable-se::before {
        content: "";
        position: absolute;
        right: 2px;
        bottom: 2px;
        width: 10px;
        height: 10px;
        border-right: 2px solid rgba(255,255,255,0.9);
        border-bottom: 2px solid rgba(255,255,255,0.9);
        opacity: 0.9;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setShouldRender(true);
      setIsClosing(false);
      return undefined;
    }

    if (!shouldRender) return undefined;

    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, closeDurationMs);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [closeDurationMs, isOpen, shouldRender]);

  useEffect(() => {
    if (isOpen || typeof window === "undefined") return undefined;

    const positionStyle = defaultPosition || { right: 20, bottom: 90 };
    const sizeStyle = defaultSize || { width: 420, height: 520 };
    const width = parsePanelNumber(sizeStyle.width, 420);
    const height = parsePanelNumber(sizeStyle.height, 520);
    const right = parsePanelNumber(positionStyle.right, 20);
    const bottom = parsePanelNumber(positionStyle.bottom, 90);
    const left = positionStyle.left != null
      ? parsePanelNumber(positionStyle.left, 20)
      : Math.max(12, window.innerWidth - width - right);
    const top = positionStyle.top != null
      ? parsePanelNumber(positionStyle.top, 20)
      : Math.max(12, window.innerHeight - height - bottom);
    const resetLayout = { left, top, width, height };

    liveLayoutRef.current = resetLayout;
    setPanelLayout(resetLayout);
    return undefined;
  }, [defaultPosition, defaultSize, isOpen]);

  useEffect(() => {
    if (!isOpen || disableDragResize || panelLayout || typeof window === "undefined") return undefined;
    const positionStyle = defaultPosition || { right: 20, bottom: 90 };
    const sizeStyle = defaultSize || { width: 420, height: 520 };
    const width = parsePanelNumber(sizeStyle.width, 420);
    const height = parsePanelNumber(sizeStyle.height, 520);
    const right = parsePanelNumber(positionStyle.right, 20);
    const bottom = parsePanelNumber(positionStyle.bottom, 90);
    const left = positionStyle.left != null
      ? parsePanelNumber(positionStyle.left, 20)
      : Math.max(12, window.innerWidth - width - right);
    const top = positionStyle.top != null
      ? parsePanelNumber(positionStyle.top, 20)
      : Math.max(12, window.innerHeight - height - bottom);
    const initialLayout = { left, top, width, height };
    liveLayoutRef.current = initialLayout;
    setPanelLayout(initialLayout);
    return undefined;
  }, [defaultPosition, defaultSize, disableDragResize, isOpen, panelLayout]);

  useEffect(() => {
    if (panelLayout) {
      liveLayoutRef.current = panelLayout;
    }
  }, [panelLayout]);

  const emitLayoutChange = useCallback((next) => {
    if (typeof onLayoutChange !== "function") return;
    if (layoutFrameRef.current) {
      window.cancelAnimationFrame(layoutFrameRef.current);
    }
    layoutFrameRef.current = window.requestAnimationFrame(() => {
      layoutFrameRef.current = null;
      onLayoutChange(next);
    });
  }, [onLayoutChange]);

  const scheduleResizeLayoutChange = useCallback((next) => {
    if (typeof onLayoutChange !== "function") return;
    if (resizeNotifyTimerRef.current) {
      window.clearTimeout(resizeNotifyTimerRef.current);
    }
    resizeNotifyTimerRef.current = window.setTimeout(() => {
      resizeNotifyTimerRef.current = null;
      emitLayoutChange(next);
    }, 90);
  }, [emitLayoutChange, onLayoutChange]);

  const clampLayoutToViewport = useCallback((nextLayout) => {
    if (typeof window === "undefined") return nextLayout;
    const margin = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = clampValue(nextLayout.width, minWidth, Math.max(minWidth, viewportWidth - margin * 2));
    const height = clampValue(nextLayout.height, minHeight, Math.max(minHeight, viewportHeight - margin * 2));
    const maxLeft = Math.max(margin, viewportWidth - width - margin);
    const maxTop = Math.max(margin, viewportHeight - height - margin);

    return {
      left: clampValue(nextLayout.left, margin, maxLeft),
      top: clampValue(nextLayout.top, margin, maxTop),
      width,
      height,
    };
  }, [minHeight, minWidth]);

  const beginPointerSession = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    document.body.style.userSelect = "none";
  }, []);

  const finishPointerSession = useCallback(() => {
    dragSessionRef.current = null;
    resizeSessionRef.current = null;
    document.body.style.userSelect = "";
  }, []);

  const applyLayoutToPanel = useCallback((nextLayout) => {
    const panel = panelRef.current;
    if (!panel || !nextLayout) return;
    panel.style.left = `${nextLayout.left}px`;
    panel.style.top = `${nextLayout.top}px`;
    panel.style.width = `${nextLayout.width}px`;
    panel.style.height = `${nextLayout.height}px`;
  }, []);

  useEffect(() => {
    if (!isOpen || isClosing || disableDragResize) return undefined;

    const handlePointerMove = (event) => {
      if (dragSessionRef.current) {
        const { origin, startX, startY } = dragSessionRef.current;
        const nextLayout = clampLayoutToViewport({
          ...origin,
          left: origin.left + (event.clientX - startX),
          top: origin.top + (event.clientY - startY),
        });
        liveLayoutRef.current = nextLayout;
        applyLayoutToPanel(nextLayout);
        return;
      }

      if (!resizeSessionRef.current) return;

      const { direction, origin, startX, startY } = resizeSessionRef.current;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      let nextLeft = origin.left;
      let nextTop = origin.top;
      let nextWidth = origin.width;
      let nextHeight = origin.height;

      if (direction.includes("e")) nextWidth = origin.width + dx;
      if (direction.includes("s")) nextHeight = origin.height + dy;
      if (direction.includes("w")) {
        nextWidth = origin.width - dx;
        nextLeft = origin.left + dx;
      }
      if (direction.includes("n")) {
        nextHeight = origin.height - dy;
        nextTop = origin.top + dy;
      }

      if (nextWidth < minWidth) {
        if (direction.includes("w")) {
          nextLeft -= minWidth - nextWidth;
        }
        nextWidth = minWidth;
      }
      if (nextHeight < minHeight) {
        if (direction.includes("n")) {
          nextTop -= minHeight - nextHeight;
        }
        nextHeight = minHeight;
      }

      const nextLayout = clampLayoutToViewport({
        left: nextLeft,
        top: nextTop,
        width: nextWidth,
        height: nextHeight,
      });

      liveLayoutRef.current = nextLayout;
      applyLayoutToPanel(nextLayout);
      scheduleResizeLayoutChange({
        width: nextLayout.width,
        height: nextLayout.height,
      });
    };

    const handlePointerUp = () => {
      const finalLayout = liveLayoutRef.current;
      if (finalLayout) {
        setPanelLayout(finalLayout);
        emitLayoutChange({
          width: finalLayout.width,
          height: finalLayout.height,
        });
      }
      finishPointerSession();
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      finishPointerSession();
      if (layoutFrameRef.current) {
        window.cancelAnimationFrame(layoutFrameRef.current);
        layoutFrameRef.current = null;
      }
      if (resizeNotifyTimerRef.current) {
        window.clearTimeout(resizeNotifyTimerRef.current);
        resizeNotifyTimerRef.current = null;
      }
    };
  }, [
    applyLayoutToPanel,
    clampLayoutToViewport,
    disableDragResize,
    emitLayoutChange,
    finishPointerSession,
    isClosing,
    isOpen,
    scheduleResizeLayoutChange,
  ]);

  const handleHeaderPointerDown = useCallback((event) => {
    if (disableDragResize || !panelLayout || event.button !== 0) return;
    beginPointerSession(event);
    dragSessionRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      origin: panelLayout,
    };
  }, [beginPointerSession, disableDragResize, panelLayout]);

  const handleResizePointerDown = useCallback((direction, event) => {
    if (disableDragResize || !panelLayout || event.button !== 0) return;
    beginPointerSession(event);
    resizeSessionRef.current = {
      direction,
      startX: event.clientX,
      startY: event.clientY,
      origin: panelLayout,
    };
  }, [beginPointerSession, disableDragResize, panelLayout]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || typeof onLayoutChange !== "function") return undefined;

    const emitLayout = () => {
      if (layoutFrameRef.current) {
        window.cancelAnimationFrame(layoutFrameRef.current);
      }
      layoutFrameRef.current = window.requestAnimationFrame(() => {
        layoutFrameRef.current = null;
        onLayoutChange({
          width: panel.offsetWidth,
          height: panel.offsetHeight,
        });
      });
    };

    emitLayout();

    if (typeof ResizeObserver === "undefined") return undefined;
    resizeObserverRef.current = new ResizeObserver(() => emitLayout());
    resizeObserverRef.current.observe(panel);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (layoutFrameRef.current) {
        window.cancelAnimationFrame(layoutFrameRef.current);
        layoutFrameRef.current = null;
      }
      if (resizeNotifyTimerRef.current) {
        window.clearTimeout(resizeNotifyTimerRef.current);
        resizeNotifyTimerRef.current = null;
      }
    };
  }, [onLayoutChange, shouldRender, isOpen]);

  if (!shouldRender) return null;

  const positionStyle = defaultPosition || { right: 20, bottom: 90 };
  const sizeStyle = defaultSize || { width: 420, height: 520 };
  const effectiveLayout = panelLayout
    ? {
        left: panelLayout.left,
        top: panelLayout.top,
        width: panelLayout.width,
        height: panelLayout.height,
      }
    : {
        ...positionStyle,
        ...sizeStyle,
      };

  return (
    <div
      ref={panelRef}
      className={["live-floating-panel", className].join(" ").trim()}
      role="dialog"
      aria-label={title || "Panel flotante"}
      style={{
        position: "fixed",
        ...effectiveLayout,
        background: "#f7fafc",
        borderRadius: 12,
        border: "1px solid #dce6f2",
        boxShadow: "0 20px 50px rgba(2, 20, 45, 0.2)",
        zIndex,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        opacity: isOpen && !isClosing ? 1 : 0,
        transform: isOpen && !isClosing ? "translateY(0) scale(1)" : "translateY(12px) scale(0.985)",
        transition: `opacity ${closeDurationMs}ms ease, transform ${closeDurationMs}ms ease`,
        pointerEvents: isOpen && !isClosing ? "auto" : "none",
      }}
    >
        <div
        className="live-panel-header"
        onPointerDown={handleHeaderPointerDown}
        style={{
          padding: "10px 12px",
          background: "#006699",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "move",
          fontWeight: 600,
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <span>{title}</span>
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={onClose}
          aria-label="Cerrar panel"
          style={{
            border: "none",
            background: "transparent",
            color: "#fff",
            fontSize: 18,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>
      <div
        style={{
          padding: 12,
          overflow: "auto",
          height: "100%",
        }}
      >
        {children}
      </div>
      {!disableDragResize && ["n", "e", "s", "w", "ne", "nw", "se", "sw"].map((direction) => (
        <div
          key={direction}
          className={`live-floating-panel-resize-handle live-floating-panel-resize-handle--${direction}`}
          onPointerDown={(event) => handleResizePointerDown(direction, event)}
          style={{ touchAction: "none" }}
        />
      ))}
    </div>
  );
};

export default LiveFloatingPanel;
